import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtDate } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { printDoc, esc } from "@/lib/print";
import { ReceiptText, Wallet, Download, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Statements() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pay, setPay] = useState(null);

  const load = () => api.get("/corporate/statements").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = async (row) => {
    try { const { data } = await api.get(`/corporate/statements/${row.id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPay = (row) => setPay({ customer: row, amount: "", method: "cash", reference: "", note: "" });
  const flat = () => rows.map((r) => ({
    customer: r.name, current: r.aging.current, d31_60: r.aging.d31_60, d61_90: r.aging.d61_90,
    d90_plus: r.aging.d90_plus, balance: r.balance, credit_limit: r.credit_limit,
    invoice_count: r.invoice_count, last_payment: r.last_payment,
  }));
  const exportCsv = () => {
    if (!rows.length) return toast.info("No statements to export");
    downloadCSV("corporate-statements.csv", flat(), [
      { key: "customer", label: "Customer" }, { key: "current", label: "Current" },
      { key: "d31_60", label: "31-60" }, { key: "d61_90", label: "61-90" }, { key: "d90_plus", label: "90+" },
      { key: "balance", label: "Balance" }, { key: "credit_limit", label: "Credit limit" },
      { key: "invoice_count", label: "Invoices" }, { key: "last_payment", label: "Last payment" },
    ]);
  };
  const printStatements = () => {
    if (!rows.length) return toast.info("No statements to print");
    const total = rows.reduce((s, r) => s + (r.balance || 0), 0);
    const body = `<h1>MobiMed — Corporate Statements Aging</h1><div class="muted">${new Date().toLocaleDateString()}</div>
      <table><thead><tr><th>Customer</th><th class="r">Current</th><th class="r">31-60</th><th class="r">61-90</th><th class="r">90+</th><th class="r">Balance</th><th class="r">Limit</th></tr></thead>
      <tbody>${rows.map((r) => `<tr><td>${esc(r.name)}</td><td class="r">${fmtGHS(r.aging.current)}</td><td class="r">${fmtGHS(r.aging.d31_60)}</td><td class="r">${fmtGHS(r.aging.d61_90)}</td><td class="r">${fmtGHS(r.aging.d90_plus)}</td><td class="r">${fmtGHS(r.balance)}</td><td class="r">${fmtGHS(r.credit_limit)}</td></tr>`).join("")}</tbody>
      <tfoot><tr><td colspan="5" class="r">Total outstanding</td><td class="r">${fmtGHS(total)}</td><td></td></tr></tfoot></table>`;
    printDoc("Corporate Statements", body);
  };
  const doPay = async () => {
    if (!(Number(pay.amount) > 0)) return toast.error("Enter a payment amount");
    try {
      await api.post(`/corporate/${pay.customer.id}/payments`, { amount: Number(pay.amount), method: pay.method, reference: pay.reference, note: pay.note });
      toast.success("Payment recorded");
      const cust = pay.customer; setPay(null); load();
      if (detail?.customer?.id === cust.id) openDetail(cust);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Corporate Statements" subtitle="Monthly statements and aging for credit customers — chase overdue balances"
        actions={<>
          <Button variant="outline" data-testid="export-statements-btn" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          <Button variant="outline" data-testid="print-statements-btn" onClick={printStatements}><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
        </>} />
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Customer</th><th className="py-2.5 px-3 text-right">Current</th><th className="py-2.5 px-3 text-right">31–60</th><th className="py-2.5 px-3 text-right">61–90</th><th className="py-2.5 px-3 text-right">90+</th><th className="py-2.5 px-3 text-right">Balance</th><th className="py-2.5 px-3 text-right">Limit</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><ReceiptText className="h-8 w-8 mx-auto mb-2 opacity-40" />No corporate customers</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" data-testid={`statement-row-${r.id}`} onClick={() => openDetail(r)}>
                <td className="py-2 px-3 font-medium text-foreground">{r.name}</td>
                <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(r.aging.current)}</td>
                <td className="py-2 px-3 text-right font-mono text-xs text-amber-600">{fmtGHS(r.aging.d31_60)}</td>
                <td className="py-2 px-3 text-right font-mono text-xs text-orange-600">{fmtGHS(r.aging.d61_90)}</td>
                <td className="py-2 px-3 text-right font-mono text-xs text-rose-600">{fmtGHS(r.aging.d90_plus)}</td>
                <td className="py-2 px-3 text-right font-mono font-semibold">{fmtGHS(r.balance)}</td>
                <td className="py-2 px-3 text-right font-mono text-xs text-muted-foreground">{fmtGHS(r.credit_limit)}</td>
                <td className="py-2 px-3 text-right">{can("finance.statements") && <Button size="sm" variant="outline" data-testid={`record-payment-${r.id}`} onClick={(e) => { e.stopPropagation(); openPay(r); }}><Wallet className="h-3.5 w-3.5 mr-1" /> Payment</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.customer?.name} — Statement</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-lg p-2" style={{ background: "var(--mm-surface-raised)" }}><div className="text-muted-foreground">Current</div><div className="font-mono font-semibold">{fmtGHS(detail.aging.current)}</div></div>
                <div className="rounded-lg p-2 text-amber-600" style={{ background: "var(--mm-surface-raised)" }}><div className="text-muted-foreground">31–60</div><div className="font-mono font-semibold">{fmtGHS(detail.aging.d31_60)}</div></div>
                <div className="rounded-lg p-2 text-orange-600" style={{ background: "var(--mm-surface-raised)" }}><div className="text-muted-foreground">61–90</div><div className="font-mono font-semibold">{fmtGHS(detail.aging.d61_90)}</div></div>
                <div className="rounded-lg p-2 text-rose-600" style={{ background: "var(--mm-surface-raised)" }}><div className="text-muted-foreground">90+</div><div className="font-mono font-semibold">{fmtGHS(detail.aging.d90_plus)}</div></div>
              </div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Outstanding balance</span><span className="font-mono font-semibold">{fmtGHS(detail.customer.balance)}</span></div>
              <div className="overflow-x-auto border-y border-border">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground text-left"><tr><th className="py-2 px-2">Date</th><th className="py-2 px-2">Ref</th><th className="py-2 px-2">Description</th><th className="py-2 px-2 text-right">Debit</th><th className="py-2 px-2 text-right">Credit</th><th className="py-2 px-2 text-right">Balance</th></tr></thead>
                  <tbody>
                    {detail.transactions.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No transactions</td></tr>}
                    {detail.transactions.map((t, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-2 px-2 text-xs text-muted-foreground">{fmtDate(t.date)}</td>
                        <td className="py-2 px-2 font-mono text-xs">{t.ref}</td>
                        <td className="py-2 px-2 text-xs">{t.description}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs">{t.debit ? fmtGHS(t.debit) : "—"}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs text-emerald-600">{t.credit ? fmtGHS(t.credit) : "—"}</td>
                        <td className="py-2 px-2 text-right font-mono text-xs font-semibold">{fmtGHS(t.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {detail && can("finance.statements") && <DialogFooter><Button data-testid="detail-record-payment-btn" onClick={() => openPay(detail.customer)}><Wallet className="h-4 w-4 mr-1.5" /> Record payment</Button></DialogFooter>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record payment — {pay?.customer?.name}</DialogTitle></DialogHeader>
          {pay && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount (GHS)</Label><Input type="number" step="0.01" data-testid="payment-amount-input" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} /></div>
                <div><Label>Method</Label>
                  <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                    <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="momo">Mobile Money</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Reference</Label><Input data-testid="payment-ref-input" value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} /></div>
              <div><Label>Note</Label><Input value={pay.note} onChange={(e) => setPay((p) => ({ ...p, note: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPay(null)}>Cancel</Button><Button data-testid="submit-payment-btn" onClick={doPay}>Save payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
